import React, { useEffect, useState, useCallback } from 'react';
import { useOutlet } from '../lib/OutletContext';
import {
  ChefHat,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Package,
  Loader2,
  Flame,
  Snowflake,
  Wine,
  IceCream,
  HelpCircle,
  Save,
  CheckCircle2,
} from 'lucide-react';
import { formatNumber } from '../lib/utils';
import api from '../lib/api';
import { cn } from '../lib/utils';
import logger from '../lib/logger';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const SECTION_ICONS = {
  'Hot Kitchen': Flame,
  'Cold Kitchen': Snowflake,
  Bar: Wine,
  Dessert: IceCream,
  Unassigned: HelpCircle,
};

const SECTION_COLORS = {
  'Hot Kitchen': { text: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30' },
  'Cold Kitchen': { text: 'text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-500/30' },
  Bar: { text: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30' },
  Dessert: { text: 'text-pink-400', bg: 'bg-pink-500/10', border: 'border-pink-500/30' },
  Unassigned: { text: 'text-zinc-400', bg: 'bg-zinc-500/10', border: 'border-zinc-500/30' },
};

const CONFIDENCE_STYLES = {
  high: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  medium: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  low: 'bg-red-500/10 text-red-300 border-red-500/30',
};

const REASON_LABELS = {
  SALES_DEMAND_SIGNAL: 'Driven by POS demand',
  SALES_HIGH_CONFIDENCE: 'High confidence',
  SALES_MEDIUM_CONFIDENCE: 'Medium confidence',
  SALES_LOW_CONFIDENCE: 'Low confidence (advisory)',
  SALES_HIGH_VARIANCE: 'High variance — buffer applied',
  SALES_LOW_DATA: 'Limited historical data',
  SALES_NEW_ITEM: 'New menu item',
};

const PrepPlanning = () => {
  const { selectedOutlet } = useOutlet();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({});
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastDraftId, setLastDraftId] = useState(null);

  const loadPlan = useCallback(async () => {
    if (!selectedOutlet) return;
    setLoading(true);
    try {
      const res = await api.get(
        `/inventory/prep-planning/plan?outlet_id=${selectedOutlet}`
      );
      setPlan(res.data);
    } catch (err) {
      logger.error('Failed to load prep plan:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedOutlet]);

  const saveDraft = useCallback(async () => {
    if (!plan || !selectedOutlet) return;
    const items = plan.sections.flatMap((s) =>
      s.preps.map((p) => ({
        prep_id: p.prep_id,
        prep_name: p.prep_name,
        section: p.section,
        base_unit: p.base_unit,
        expected_yield: p.expected_yield,
        required_qty: p.required_qty,
        demand_required_qty: p.demand_required_qty,
        current_stock: p.current_stock,
        batches: p.batches,
        confidence: p.confidence,
        risk_flags: p.risk_flags || [],
        reason_codes: p.reason_codes || [],
        contributing_items: p.contributing_items || [],
      }))
    );
    if (!items.length) {
      toast.error('No prep items to save.');
      return;
    }
    setSavingDraft(true);
    try {
      const res = await api.post('/inventory/prep-planning/drafts', {
        outlet_id: selectedOutlet,
        target_date: plan.target_date,
        items,
      });
      const draftId = res.data?.draft?.draft_id;
      setLastDraftId(draftId || null);
      toast.success(
        `Prep draft saved (${items.length} prep${items.length !== 1 ? 's' : ''}). Kitchen confirms to produce.`
      );
    } catch (err) {
      logger.error('Failed to save prep draft:', err);
      toast.error(err?.response?.data?.detail || 'Failed to save draft.');
    } finally {
      setSavingDraft(false);
    }
  }, [plan, selectedOutlet]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const toggleExpanded = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const toggleSection = (name) =>
    setCollapsedSections((prev) => ({ ...prev, [name]: !prev[name] }));

  if (!selectedOutlet) {
    return (
      <div
        className="text-center py-12 text-zinc-400"
        data-testid="prep-planning-no-outlet"
      >
        Select an outlet to view prep planning.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="prep-planning-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1
              className="text-3xl font-black tracking-tight flex items-center gap-3"
              data-testid="prep-planning-title"
            >
              <ChefHat className="w-8 h-8 text-emerald-500" />
              Prep Planning
            </h1>
            <p className="text-sm text-zinc-400 mt-1">
              Section-wise kitchen prep recommendations from today's sales demand.
              Planning only — no stock changes.
            </p>
          </div>
          <button
            onClick={loadPlan}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#18181b] border border-[#27272a] hover:bg-[#27272a] transition-colors text-sm font-medium disabled:opacity-50"
            data-testid="prep-planning-refresh"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Refresh
          </button>
          <button
            onClick={saveDraft}
            disabled={
              savingDraft || loading || !plan || plan.summary.total_preps === 0
            }
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-600 text-black transition-colors text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="prep-planning-create-draft"
          >
            {savingDraft ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {savingDraft ? 'Saving…' : 'Create Prep Draft'}
          </button>
        </div>

        {lastDraftId && (
          <div
            className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-center gap-2 text-sm text-emerald-200"
            data-testid="prep-draft-saved-banner"
          >
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            Draft saved. Kitchen must confirm before any stock movement.
            <span className="font-mono text-xs text-emerald-300/70">
              ID {lastDraftId.slice(0, 8)}
            </span>
          </div>
        )}

        {/* Loading */}
        {loading && !plan && (
          <div
            className="text-center py-20 text-zinc-400"
            data-testid="prep-planning-loading"
          >
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
            Generating prep plan…
          </div>
        )}

        {/* Sales fetch error banner */}
        {plan?.diagnostics?.sales_demand_fetch_error && (
          <div
            className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 flex items-center gap-3"
            data-testid="prep-sales-demand-fetch-error-banner"
          >
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-200 font-medium">
                Sales demand data unavailable. No prep can be planned without it.
              </p>
              <p className="text-xs text-red-300/70 mt-0.5 font-mono break-all">
                {plan.diagnostics.sales_demand_fetch_error}
              </p>
            </div>
            <button
              onClick={loadPlan}
              className="px-3 py-1.5 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-200 text-xs font-medium border border-red-500/30 whitespace-nowrap"
              data-testid="prep-sales-demand-fetch-error-retry"
            >
              Retry
            </button>
          </div>
        )}

        {/* Unmapped sales items callout */}
        {plan?.diagnostics?.sales_demand_items_unmapped > 0 && (
          <div
            className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex items-center gap-3"
            data-testid="prep-unmapped-sales-items-callout"
          >
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-amber-200">
                <strong data-testid="prep-unmapped-sales-items-count">
                  {plan.diagnostics.sales_demand_items_unmapped}
                </strong>{' '}
                sales{' '}
                {plan.diagnostics.sales_demand_items_unmapped === 1
                  ? 'item is'
                  : 'items are'}{' '}
                not mapped to recipes — they're excluded from prep planning.
              </p>
              {plan.diagnostics.sales_demand_unmapped_item_ids?.length > 0 && (
                <p className="text-xs text-amber-300/70 mt-0.5 font-mono">
                  {plan.diagnostics.sales_demand_unmapped_item_ids
                    .slice(0, 5)
                    .join(', ')}
                  {plan.diagnostics.sales_demand_unmapped_item_ids.length > 5 &&
                    ` +${plan.diagnostics.sales_demand_unmapped_item_ids.length - 5} more`}
                </p>
              )}
            </div>
            <Link
              to="/recipes/mapping"
              className="px-3 py-1.5 rounded-md bg-amber-500/10 hover:bg-amber-500/20 text-amber-200 text-xs font-medium border border-amber-500/30 whitespace-nowrap"
              data-testid="prep-unmapped-sales-items-link"
            >
              Map items
            </Link>
          </div>
        )}

        {/* Summary cards */}
        {plan?.summary && (
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
            data-testid="prep-planning-summary-cards"
          >
            <SummaryCard
              icon={Package}
              label="Preps to Produce"
              value={plan.summary.total_preps}
              color="text-emerald-400"
              bgColor="bg-emerald-500/10"
              testId="prep-summary-total-preps"
            />
            <SummaryCard
              icon={ChefHat}
              label="Total Batches"
              value={plan.summary.total_batches}
              color="text-blue-400"
              bgColor="bg-blue-500/10"
              testId="prep-summary-total-batches"
            />
            <SummaryCard
              icon={Flame}
              label="Sections"
              value={plan.summary.sections_count}
              color="text-orange-400"
              bgColor="bg-orange-500/10"
              testId="prep-summary-sections"
            />
            <SummaryCard
              icon={AlertTriangle}
              label="Low-confidence"
              value={plan.summary.low_confidence_count}
              color="text-red-400"
              bgColor="bg-red-500/10"
              testId="prep-summary-low-confidence"
            />
          </div>
        )}

        {/* Stock-covered informational note */}
        {plan?.summary?.preps_covered_by_stock > 0 && (
          <div
            className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-3 flex items-center gap-2 text-sm text-sky-200"
            data-testid="prep-stock-covered-note"
          >
            <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-sky-400" />
            <span>
              <strong data-testid="prep-stock-covered-count">
                {plan.summary.preps_covered_by_stock}
              </strong>{' '}
              prep{plan.summary.preps_covered_by_stock !== 1 ? 's' : ''} fully
              covered by existing stock — no production needed.
            </span>
          </div>
        )}

        {/* Empty state */}
        {!loading && plan && plan.sections.length === 0 && (
          <div
            className="bg-[#18181b] border border-[#27272a] rounded-lg p-12 text-center"
            data-testid="prep-planning-empty"
          >
            <ChefHat className="w-12 h-12 text-zinc-500 mx-auto mb-3" />
            <p className="text-lg font-semibold text-white mb-1">
              No prep recommended
            </p>
            <p className="text-sm text-zinc-400 max-w-md mx-auto">
              {plan.diagnostics.sales_demand_fetch_error
                ? 'Sales demand feed is unavailable. Once reachable, your prep plan will appear here.'
                : plan.diagnostics.sales_demand_items_seen === 0
                ? 'No sales demand was recorded for this outlet today.'
                : 'Sales demand exists but none of the mapped recipes use prep components.'}
            </p>
          </div>
        )}

        {/* Section groups */}
        {plan?.sections?.map((section) => {
          const Icon = SECTION_ICONS[section.section_name] || HelpCircle;
          const colors =
            SECTION_COLORS[section.section_name] || SECTION_COLORS.Unassigned;
          const isCollapsed = collapsedSections[section.section_name];
          return (
            <div
              key={section.section_name}
              className={cn(
                'rounded-lg border bg-[#18181b]',
                colors.border
              )}
              data-testid={`prep-section-${section.section_name
                .toLowerCase()
                .replace(/\s+/g, '-')}`}
            >
              <button
                onClick={() => toggleSection(section.section_name)}
                className="w-full flex items-center justify-between p-4 hover:bg-[#1f1f23] transition-colors rounded-lg"
                data-testid={`prep-section-toggle-${section.section_name
                  .toLowerCase()
                  .replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'w-10 h-10 rounded-lg flex items-center justify-center',
                      colors.bg
                    )}
                  >
                    <Icon className={cn('w-5 h-5', colors.text)} />
                  </div>
                  <div className="text-left">
                    <h2 className="text-lg font-bold">{section.section_name}</h2>
                    <p className="text-xs text-zinc-400">
                      {section.total_preps} prep
                      {section.total_preps !== 1 ? 's' : ''} ·{' '}
                      {section.total_batches} batch
                      {section.total_batches !== 1 ? 'es' : ''}
                    </p>
                  </div>
                </div>
                {isCollapsed ? (
                  <ChevronDown className="w-5 h-5 text-zinc-400" />
                ) : (
                  <ChevronUp className="w-5 h-5 text-zinc-400" />
                )}
              </button>

              {!isCollapsed && (
                <div className="px-4 pb-4 space-y-3">
                  {section.preps.map((prep) => (
                    <PrepCard
                      key={prep.prep_id}
                      prep={prep}
                      expanded={!!expanded[prep.prep_id]}
                      onToggle={() => toggleExpanded(prep.prep_id)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
  );
};

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const SummaryCard = ({ icon: Icon, label, value, color, bgColor, testId }) => (
  <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4">
    <div className="flex items-center gap-3">
      <div
        className={cn(
          'w-10 h-10 rounded-lg flex items-center justify-center',
          bgColor
        )}
      >
        <Icon className={cn('w-5 h-5', color)} />
      </div>
      <div>
        <p className="text-xs text-zinc-400 uppercase tracking-wider">{label}</p>
        <p className={cn('text-2xl font-bold', color)} data-testid={testId}>
          {value}
        </p>
      </div>
    </div>
  </div>
);

const PrepCard = ({ prep, expanded, onToggle }) => {
  const confClass =
    CONFIDENCE_STYLES[prep.confidence] || CONFIDENCE_STYLES.medium;
  const hasStockRow =
    prep.current_stock !== null && prep.current_stock !== undefined;
  const hasDemandField =
    prep.demand_required_qty !== null &&
    prep.demand_required_qty !== undefined &&
    prep.demand_required_qty > 0;
  const stockReducedDemand =
    hasStockRow &&
    hasDemandField &&
    prep.current_stock > 0 &&
    prep.demand_required_qty > prep.required_qty;

  return (
    <div
      className="bg-[#0f0f11] border border-[#27272a] rounded-lg overflow-hidden"
      data-testid={`prep-card-${prep.prep_id}`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-[#15151a] transition-colors"
        data-testid={`prep-card-toggle-${prep.prep_id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3
              className="text-base font-bold text-white truncate"
              data-testid={`prep-name-${prep.prep_id}`}
            >
              {prep.prep_name}
            </h3>
            <span
              className={cn(
                'px-2 py-0.5 text-xs font-medium rounded-full border',
                confClass
              )}
              data-testid={`prep-confidence-${prep.prep_id}`}
            >
              {prep.confidence}
            </span>
            {stockReducedDemand && (
              <span
                className="px-2 py-0.5 text-xs font-medium rounded-full border bg-sky-500/10 text-sky-300 border-sky-500/30"
                data-testid={`prep-stock-offset-${prep.prep_id}`}
              >
                stock offset
              </span>
            )}
            {prep.risk_flags?.map((f) => (
              <span
                key={f}
                className="px-2 py-0.5 text-xs font-medium rounded-full border bg-amber-500/10 text-amber-300 border-amber-500/30"
                data-testid={`prep-risk-flag-${prep.prep_id}-${f}`}
              >
                {f.replace(/_/g, ' ').toLowerCase()}
              </span>
            ))}
          </div>
          <div className="mt-2 text-sm text-zinc-400 flex items-center gap-4 flex-wrap">
            {hasDemandField && (
              <span data-testid={`prep-demand-${prep.prep_id}`}>
                Demand:{' '}
                <strong className="text-zinc-200">
                  {formatNumber(prep.demand_required_qty, 2)} {prep.base_unit}
                </strong>
              </span>
            )}
            {hasStockRow && (
              <span data-testid={`prep-current-stock-${prep.prep_id}`}>
                On hand:{' '}
                <strong className="text-zinc-200">
                  {formatNumber(prep.current_stock, 2)} {prep.base_unit}
                </strong>
              </span>
            )}
            <span data-testid={`prep-required-${prep.prep_id}`}>
              Produce:{' '}
              <strong className="text-emerald-400">
                {formatNumber(prep.required_qty, 2)} {prep.base_unit}
              </strong>
            </span>
            <span data-testid={`prep-batches-${prep.prep_id}`}>
              Batches:{' '}
              <strong className="text-white">{prep.batches}</strong>{' '}
              <span className="text-zinc-500">
                × {formatNumber(prep.expected_yield, 2)} {prep.base_unit}
              </span>
            </span>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-zinc-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-zinc-400 flex-shrink-0" />
        )}
      </button>

      {expanded && (
        <div
          className="border-t border-[#27272a] p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-[#0a0a0c]"
          data-testid={`prep-detail-${prep.prep_id}`}
        >
          <DetailRow
            label="Confidence multiplier"
            value={formatNumber(prep.confidence_multiplier, 2)}
          />
          <DetailRow
            label="Variance buffer"
            value={`× ${formatNumber(prep.variance_buffer_multiplier, 2)}`}
          />
          <DetailRow
            label="Sales bucket"
            value={prep.sales_bucket || '—'}
          />
          <DetailRow
            label="Contributing menu items"
            value={prep.contributing_items_count}
          />
          {prep.contributing_items?.length > 0 && (
            <div className="md:col-span-2">
              <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">
                Driven by
              </p>
              <div className="flex flex-wrap gap-1.5">
                {prep.contributing_items.map((c) => (
                  <span
                    key={c}
                    className="px-2 py-0.5 text-xs rounded bg-[#18181b] border border-[#27272a] text-zinc-300"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          )}
          {prep.reason_codes?.length > 0 && (
            <div className="md:col-span-2">
              <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">
                Why
              </p>
              <ul className="space-y-1 text-sm text-zinc-300">
                {prep.reason_codes.map((r) => (
                  <li
                    key={r}
                    className="flex items-center gap-2"
                    data-testid={`prep-reason-${prep.prep_id}-${r}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    {REASON_LABELS[r] || r}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase tracking-wider text-zinc-500">{label}</p>
    <p className="text-sm text-white font-medium">{value}</p>
  </div>
);

export default PrepPlanning;
